import { EllipsisHorizontal, Phone } from "@medusajs/icons"
import { Button, Drawer, DropdownMenu, Input, Label, Prompt, Text, toast } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { CodeInput } from "@/components/auth-shell"
import { errorMessage, sdk } from "@/lib/sdk"
import { Badge, Banner } from "@/terabizz"

/**
 * Your authenticator apps (Settings → Your account → Two-step verification), backend
 * lib/staff/authenticators.ts. Up to 3 apps (e.g. a second phone), one main and the rest backups;
 * a code from any of them signs in. Adding or removing one asks for your password or a code from an
 * app already on the account. A new app's key is shown only while it's being added.
 */
const BASE = "/admin/terify/me/2fa/authenticators"

export type AuthenticatorApp = { id: string; name: string; is_primary: boolean; created_at: string; last_used_at: string | null }
type AppsInfo = { authenticators: AuthenticatorApp[]; max: number; required: boolean }
type Confirm = { password: string } | { verification_code: string }

const day = (s: string) => new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
function ago(s: string) {
  const m = Math.round((Date.now() - new Date(s).getTime()) / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} h ago`
  const d = Math.round(h / 24)
  return d < 14 ? `${d} day${d === 1 ? "" : "s"} ago` : day(s)
}

export function useAuthenticatorApps(enabled = true) {
  return useQuery({ queryKey: ["2fa-apps"], queryFn: () => sdk.client.fetch<AppsInfo>(BASE), enabled })
}

export function AuthenticatorApps() {
  const qc = useQueryClient()
  const q = useAuthenticatorApps()
  const [adding, setAdding] = useState(false)
  const [renaming, setRenaming] = useState<AuthenticatorApp | null>(null)
  const [removing, setRemoving] = useState<AuthenticatorApp | null>(null)
  const apps = q.data?.authenticators ?? []
  const max = q.data?.max ?? 3
  const refresh = () => (qc.invalidateQueries({ queryKey: ["2fa-apps"] }), qc.invalidateQueries({ queryKey: ["2fa"] }))
  const makeMain = useMutation({
    mutationFn: (a: AuthenticatorApp) => sdk.client.fetch(`${BASE}/${a.id}`, { method: "POST", body: { action: "make_main" } }),
    onSuccess: (_r, a) => (refresh(), toast.success(`"${a.name}" is now your main authenticator app`)),
    onError: (e) => toast.error(errorMessage(e)),
  })

  if (q.isError) return <Text size="xsmall" className="text-ui-fg-error">{errorMessage(q.error)}</Text>
  return (
    <div className="flex flex-col gap-3" data-testid="authenticator-apps">
      <div>
        <Text size="small" weight="plus">Authenticator apps</Text>
        <Text size="xsmall" className="text-ui-fg-subtle">A code from any of these signs you in. Add one on a second phone so losing a phone doesn't lock you out.</Text>
      </div>
      <ul className="divide-y divide-ui-border-base overflow-hidden rounded-lg border border-ui-border-base">
        {q.isLoading ? <li className="h-[58px] animate-pulse bg-ui-bg-subtle" /> : null}
        {apps.map((a) => (
          <li key={a.id} className="flex items-center gap-3 px-3 py-2.5">
            <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-md bg-ui-bg-subtle text-ui-fg-subtle">
              <Phone />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <Text size="small" weight="plus" className="truncate" title={a.name}>{a.name}</Text>
                <Badge tone={a.is_primary ? "success" : "neutral"}>{a.is_primary ? "Main" : "Backup"}</Badge>
              </div>
              <Text size="xsmall" className="text-ui-fg-subtle">
                Added {day(a.created_at)} · {a.last_used_at ? `Last used ${ago(a.last_used_at)}` : "Not used yet"}
              </Text>
            </div>
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <button type="button" aria-label={`Options for ${a.name}`} className="flex size-8 shrink-0 items-center justify-center rounded-lg text-ui-fg-subtle hover:bg-ui-bg-base-hover">
                  <EllipsisHorizontal />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item onClick={() => setRenaming(a)}>Rename</DropdownMenu.Item>
                {!a.is_primary ? <DropdownMenu.Item onClick={() => makeMain.mutate(a)}>Make main</DropdownMenu.Item> : null}
                <DropdownMenu.Separator />
                <DropdownMenu.Item className="text-ui-fg-error" onClick={() => setRemoving(a)}>Remove</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </li>
        ))}
      </ul>
      {q.data ? (
        apps.length < max ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Button size="small" variant="secondary" onClick={() => setAdding(true)}>Add another authenticator</Button>
            <Text size="xsmall" className="text-ui-fg-subtle">{apps.length} of {max} used</Text>
          </div>
        ) : (
          <Text size="xsmall" className="text-ui-fg-subtle">You have {max} authenticator apps, the most allowed. Remove one to add another.</Text>
        )
      ) : null}

      {adding && q.data ? <AddAuthenticator existing={apps} onClose={() => setAdding(false)} onAdded={refresh} /> : null}
      {renaming ? <RenameAuthenticator app={renaming} onClose={() => setRenaming(null)} onDone={refresh} /> : null}
      {removing && q.data ? <RemoveAuthenticator app={removing} apps={apps} required={q.data.required} onClose={() => setRemoving(null)} onDone={refresh} onAdd={() => (setRemoving(null), setAdding(true))} /> : null}
    </div>
  )
}

// ── Confirm it's you: password, or a code from an app already on the account ──

function ConfirmItsYou({ onChange, idPrefix }: { onChange: (c: Confirm | null) => void; idPrefix: string }) {
  const [mode, setMode] = useState<"password" | "code">("password")
  const [text, setText] = useState("")
  useEffect(() => {
    const t = text.trim()
    onChange(!t ? null : mode === "password" ? { password: text } : /^\d{6}$/.test(t.replace(/\s/g, "")) ? { verification_code: t.replace(/\s/g, "") } : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, text])
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1">
        <Text size="small" weight="plus">Confirm it's you</Text>
      </legend>
      <div role="radiogroup" aria-label="Confirm with" className="inline-flex w-fit rounded-lg bg-ui-bg-subtle p-0.5">
        {(["password", "code"] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            onClick={() => (setMode(m), setText(""))}
            className={`rounded-md px-3 py-1 text-[13px] ${mode === m ? "bg-ui-bg-base text-ui-fg-base shadow-borders-base" : "text-ui-fg-subtle hover:text-ui-fg-base"}`}
          >
            {m === "password" ? "Password" : "Code from an app"}
          </button>
        ))}
      </div>
      {mode === "password" ? (
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor={`${idPrefix}-password`}>Your password</Label>
          <Input id={`${idPrefix}-password`} type="password" autoComplete="current-password" value={text} onChange={(e) => setText(e.target.value)} />
        </div>
      ) : (
        <div className="flex flex-col gap-y-1.5">
          <Label htmlFor={`${idPrefix}-code`}>Code from an authenticator app already on your account</Label>
          <Input id={`${idPrefix}-code`} inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123456" className="w-40 font-mono" value={text} onChange={(e) => setText(e.target.value)} />
        </div>
      )}
    </fieldset>
  )
}

// ── Add another authenticator ────────────────────────────────────────────────

function AddAuthenticator({ existing, onClose, onAdded }: { existing: AuthenticatorApp[]; onClose: () => void; onAdded: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string } | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [name, setName] = useState(existing.length > 1 ? `Backup phone ${existing.length}` : "Backup phone")
  const [code, setCode] = useState("")
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const start = useMutation({
    mutationFn: () => sdk.client.fetch<{ secret: string; otpauth_url: string }>(BASE, { method: "POST", body: { action: "start" } }),
    onSuccess: setSetup,
    onError: (e) => (toast.error(errorMessage(e)), onClose()),
  })
  const add = useMutation({
    mutationFn: () => sdk.client.fetch<{ authenticator: AuthenticatorApp }>(BASE, { method: "POST", body: { action: "add", name: name.trim(), code, ...(confirm ?? {}) } }),
    onSuccess: (r) => (onAdded(), toast.success(`Added "${r.authenticator.name}". Codes from it now sign you in.`), onClose()),
    onError: () => setCode(""),
  })
  // One key per opening (a ref, so React's dev double-run doesn't make two).
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    start.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (!setup) return setQr(null)
    import("qrcode").then((m) => m.toDataURL(setup.otpauth_url, { margin: 1, width: 176 }).then(setQr)).catch(() => setQr(null))
  }, [setup])
  const ready = !!setup && name.trim().length > 0 && code.length === 6 && !!confirm

  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content aria-describedby={undefined}>
        <form className="flex h-full min-h-0 flex-col" onSubmit={(e) => (e.preventDefault(), ready && !add.isPending && add.mutate())}>
          <Drawer.Header>
            <Drawer.Title>Add another authenticator</Drawer.Title>
          </Drawer.Header>
          <Drawer.Body className="flex flex-col gap-6 overflow-y-auto">
            <Text size="small" className="text-ui-fg-subtle">
              Set up an authenticator app on another phone (or a password manager). Once it's added, a code from it or from your other apps signs you in.
            </Text>

            <section className="flex flex-col gap-3">
              <Text size="small" weight="plus">1. Scan this QR code with the new app</Text>
              <div className="flex flex-col items-center gap-2">
                {/* Always dark on white, so phones can scan it in dark mode too. */}
                {qr ? <img src={qr} alt="QR code for your new authenticator app" width={176} height={176} className="rounded-lg border border-ui-border-base bg-white p-1" /> : <div className="size-[176px] animate-pulse rounded-lg bg-ui-bg-subtle" />}
                {setup ? (
                  <Text size="xsmall" className="text-center text-ui-fg-subtle">
                    Can't scan? Enter this key instead:
                    <br />
                    <button type="button" title="Copy" data-testid="authenticator-key" className="mt-1 select-all break-all font-mono text-ui-fg-base hover:underline" onClick={() => navigator.clipboard.writeText(setup.secret).then(() => toast.success("Key copied"))}>
                      {setup.secret.replace(/(.{4})/g, "$1 ").trim()}
                    </button>
                  </Text>
                ) : null}
                <Text size="xsmall" className="text-center text-ui-fg-muted">This key is shown only now. It stops working if you close this without adding the app.</Text>
              </div>
            </section>

            <section className="flex flex-col gap-y-1.5">
              <Label htmlFor="new-app-name" weight="plus">2. Name it</Label>
              <Input id="new-app-name" maxLength={60} placeholder="e.g. Pixel 8, work iPhone" value={name} onChange={(e) => setName(e.target.value)} />
              <Text size="xsmall" className="text-ui-fg-subtle">So you can tell your apps apart later.</Text>
            </section>

            <section className="flex flex-col gap-2">
              <Text size="small" weight="plus">3. Enter the 6-digit code the new app shows</Text>
              <CodeInput value={code} onChange={setCode} disabled={!setup || add.isPending} label="Code from the new app" />
            </section>

            <section className="border-t border-ui-border-base pt-4">
              <ConfirmItsYou onChange={setConfirm} idPrefix="add-app" />
            </section>

            {add.error ? <Banner tone="critical">{errorMessage(add.error)}</Banner> : null}
          </Drawer.Body>
          <Drawer.Footer>
            <Button type="button" variant="secondary" size="small" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="small" isLoading={add.isPending} disabled={!ready}>Add authenticator</Button>
          </Drawer.Footer>
        </form>
      </Drawer.Content>
    </Drawer>
  )
}

// ── Rename ───────────────────────────────────────────────────────────────────

function RenameAuthenticator({ app, onClose, onDone }: { app: AuthenticatorApp; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(app.name)
  const save = useMutation({
    mutationFn: () => sdk.client.fetch(`${BASE}/${app.id}`, { method: "POST", body: { action: "rename", name: name.trim() } }),
    onSuccess: () => (onDone(), toast.success("Renamed"), onClose()),
  })
  return (
    <Prompt open onOpenChange={(o) => !o && onClose()}>
      <Prompt.Content>
        <form onSubmit={(e) => (e.preventDefault(), name.trim() && save.mutate())}>
          <Prompt.Header>
            <Prompt.Title>Rename authenticator app</Prompt.Title>
            <Prompt.Description>Use the phone or app it's on, so you can tell your apps apart.</Prompt.Description>
          </Prompt.Header>
          <div className="flex flex-col gap-y-1.5 px-4 pb-4 pt-4">
            <Label htmlFor="rename-app">Name</Label>
            <Input id="rename-app" autoFocus maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
            {save.error ? <Text size="small" className="text-ui-fg-error">{errorMessage(save.error)}</Text> : null}
          </div>
          <Prompt.Footer>
            <Prompt.Cancel type="button">Cancel</Prompt.Cancel>
            <Button type="submit" size="small" disabled={!name.trim()} isLoading={save.isPending}>Save</Button>
          </Prompt.Footer>
        </form>
      </Prompt.Content>
    </Prompt>
  )
}

// ── Remove ───────────────────────────────────────────────────────────────────

function RemoveAuthenticator({ app, apps, required, onClose, onDone, onAdd }: { app: AuthenticatorApp; apps: AuthenticatorApp[]; required: boolean; onClose: () => void; onDone: () => void; onAdd: () => void }) {
  const [confirm, setConfirm] = useState<Confirm | null>(null)
  const remove = useMutation({
    mutationFn: () => sdk.client.fetch(`${BASE}/${app.id}`, { method: "POST", body: { action: "remove", ...(confirm ?? {}) } }),
    onSuccess: () => (onDone(), toast.success(`Removed "${app.name}"`), onClose()),
  })
  const last = apps.length <= 1
  const next = app.is_primary ? apps.filter((a) => a.id !== app.id).sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at))[0] : null

  if (last) {
    return (
      <Prompt open onOpenChange={(o) => !o && onClose()}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Add another app first</Prompt.Title>
            <Prompt.Description>
              {required
                ? `"${app.name}" is your only authenticator app, and this store requires two-step verification. Add another authenticator app, then remove this one.`
                : `"${app.name}" is your only authenticator app. Add another one first, or turn two-step verification off.`}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel type="button">Close</Prompt.Cancel>
            <Button type="button" size="small" onClick={onAdd}>Add another authenticator</Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    )
  }
  return (
    <Prompt open onOpenChange={(o) => !o && onClose()}>
      <Prompt.Content>
        <form onSubmit={(e) => (e.preventDefault(), confirm && !remove.isPending && remove.mutate())}>
          <Prompt.Header>
            <Prompt.Title>Remove "{app.name}"?</Prompt.Title>
            <Prompt.Description>
              Codes from this app will stop working for your account.{next ? ` "${next.name}" becomes your main authenticator app.` : ""} If the phone was lost or stolen, also change your password.
            </Prompt.Description>
          </Prompt.Header>
          <div className="flex flex-col gap-y-3 px-4 pb-4 pt-4">
            <ConfirmItsYou onChange={setConfirm} idPrefix="remove-app" />
            {remove.error ? <Text size="small" className="text-ui-fg-error">{errorMessage(remove.error)}</Text> : null}
          </div>
          <Prompt.Footer>
            <Prompt.Cancel type="button">Cancel</Prompt.Cancel>
            <Button type="submit" size="small" variant="danger" disabled={!confirm} isLoading={remove.isPending}>Remove</Button>
          </Prompt.Footer>
        </form>
      </Prompt.Content>
    </Prompt>
  )
}
